import React, { useMemo, useCallback, useEffect } from "react";
import {
  ProductDetailDTO,
  ProgramScheduleDTO,
} from "@campgladiator/cg-common.types.types";
import {
  getFormattedWeekDays,
  militaryTimeToMeridianTime,
} from "@campgladiator/cg-common.utility.dates";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import { SelectTrainerInputProps } from "../../SelectTrainerInput";
import { getProgramApprovedTypeBySelectedProductDetail } from "../../SelectTrainerInput/logic";
import { SelectedTrainer } from "../../SelectTrainerInput/SelectTrainerInput";
import { filterProgramScheduleByProductSubType } from "../../UpgradeMembershipModal/components/SelectSchedule/logic";
import { CreateMembershipSelectProductProps } from "../components/CreateMembershipSelectProduct";

const OUTDOOR_SCHEDULES_MAP_URL =
  "https://campgladiator.com/programs/camp/outdoor-locations";
const ONLINE_SCHEDULES_MAP_URL =
  "https://campgladiator.com/membership/bold-plus/select-schedule?productType=BOOTCAMP&productSubType=ONLINE";

export type UseCreateMembershipModalSelectProductStepProps = {
  couponOptions: Option[];
  handleOnSelectCoupon: (option?: Option) => void;
  productDetails: ProductDetailDTO[];
  productOptions: Option[];
  selectedProductOption: Option;
  selectedTrainer: SelectedTrainer;
  selectedTrainerSchedules: ProgramScheduleDTO[];
  setIsTrainerLoading: (isLoading: boolean) => void;
  setSelectedProductOption: (option?: Option) => void;
  setSelectedScheduleOption: (option?: Option) => void;
  setSelectedTrainer: (trainer?: SelectedTrainer) => void;
  setSelectedTrainerSchedules: (schedules: ProgramScheduleDTO[]) => void;
  setTrialDays: (days: number) => void;
  trialDays: number;
};

const useCreateMembershipModalSelectProductStep = ({
  productDetails,
  selectedProductOption,
  setSelectedTrainer,
  setSelectedProductOption,
  selectedTrainer,
  selectedTrainerSchedules,
  setIsTrainerLoading,
  setSelectedScheduleOption,
  productOptions,
  setTrialDays,
  trialDays,
  couponOptions,
  handleOnSelectCoupon,
  setSelectedTrainerSchedules,
}: UseCreateMembershipModalSelectProductStepProps) => {
  const getSelectedProductDetails = (): ProductDetailDTO => {
    try {
      const result = productDetails?.find((x) =>
        x.products?.find(
          (product) =>
            product.stripeProductId ===
            selectedProductOption?.metadata?.stripeProductId
        )
      );
      if (result) {
        return result;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  };
  const selectedProductDetails = getSelectedProductDetails();
  const selectedProductType = selectedProductDetails?.type;
  const selectedProductSubType = selectedProductDetails?.subType;
  const isScheduleRequired =
    selectedProductType === "BOOTCAMP" &&
    selectedProductSubType === "IN_PERSON";
  const isScheduleVisible = selectedProductType === "BOOTCAMP";

  const { approvedType, onlineApprovedIfOutdoor, outdoorApprovedIfOnline } =
    getProgramApprovedTypeBySelectedProductDetail(selectedProductDetails);

  const handleOnTrainerSelect = (trainer?: SelectedTrainer) => {
    setSelectedTrainer(trainer);
  };

  const handleClearTrainerSelect = () => {
    setSelectedTrainer(undefined);
  };

  const handleSelectProductOption = (option?: Option) => {
    setSelectedProductOption(option);
    handleOnTrainerSelect(selectedTrainer);
  };

  const selectTrainerInputProps: SelectTrainerInputProps = useMemo(() => {
    return {
      selectedTrainer,
      selectedProductSubType: selectedProductSubType,
      selectedProductType: selectedProductType,
      approvedProgram: [
        approvedType,
        onlineApprovedIfOutdoor,
        outdoorApprovedIfOnline,
      ],
      onItemSelect: handleOnTrainerSelect,
      onClearTrainerSearch: handleClearTrainerSelect,
      setIsTrainerLoading: setIsTrainerLoading,
      setTrainerProgramSchedules: () => {},
      isDisabled: !selectedProductOption,
    };
  }, [selectedProductDetails, selectedProductOption, selectedTrainer]);

  const scheduleOptions = useMemo(() => {
    if (!selectedTrainerSchedules || selectedTrainerSchedules.length === 0) {
      return [];
    }

    const filteredScheduleOptions = selectedTrainerSchedules.map(
      (s) =>
        ({
          itemId: s.id,
          type: "item",
          text: `${getFormattedWeekDays({
            daysOfTheWeek: s.daysOfTheWeek,
          })} - ${militaryTimeToMeridianTime(s.startTime)}`,
          metadata: s,
        } as Option)
    );

    return filteredScheduleOptions;
  }, [selectedTrainerSchedules]);

  const handleSelectScheduleOption = (option?: Option) => {
    setSelectedScheduleOption(option);
  };

  const handleTrialDaysInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(e.target.value);
    const trialDays = Math.max(0, Math.min(value, 30));
    setTrialDays(trialDays);
  };

  const schedulesMapUrl =
    selectedProductSubType === "IN_PERSON"
      ? OUTDOOR_SCHEDULES_MAP_URL
      : ONLINE_SCHEDULES_MAP_URL;

  const createMembershipSelectProductProps: CreateMembershipSelectProductProps =
    {
      handleSelectProductOption,
      productOptions,
      selectTrainerInputProps,
      scheduleOptions,
      handleSelectScheduleOption,
      handleTrialDaysInputChange,
      trialDays,
      selectedProductOption,
      couponOptions,
      handleSelectCouponOption: handleOnSelectCoupon,
      isScheduleRequired,
      isScheduleVisible,
      schedulesMapUrl,
    };

  const reLoadSchedules = useCallback(() => {
    const filteredSchedulesToShow =
      selectedProductType === "BOOTCAMP"
        ? filterProgramScheduleByProductSubType(
            selectedTrainer?.programSchedules,
            selectedProductSubType
          )
        : null;
    setSelectedTrainerSchedules(filteredSchedulesToShow);
  }, [selectedTrainer, selectedProductSubType, selectedProductType]);

  useEffect(() => {
    reLoadSchedules();
  }, [reLoadSchedules]);

  return {
    selectedProductType,
    isScheduleRequired,
    isScheduleVisible,
    selectedProductSubType,
    createMembershipSelectProductProps,
  };
};

export default useCreateMembershipModalSelectProductStep;
